import { useEntryStore } from '@/services/entryStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';

/**
 * Composable for handling mark/unmark billed functionality for time entries
 * Provides shared logic between EntryTable and TimeEntryCard components
 */
export function useMarkAsBilled(currentFilter = null) {
  const entryStore = useEntryStore();
  const {
    handleError,
    handleSuccess
  } = useSnackbarHandler();

  /**
   * Determines if an entry should be removed from the current view after billed status change
   * @param {Object} entry - The entry with updated billed status
   * @param {Object} filter - Current filter being applied
   * @returns {boolean} - Whether the entry should be removed from view
   */
  const shouldRemoveEntryAfterBilledChange = (entry, filter) => {
    // If no filter or filter allows all billed states, don't remove
    if (!filter?.billed?.id || filter.billed.id === 'all') {
      return false;
    }

    // If marking as billed and we're filtering for unbilled entries
    if (entry.billed && ['WIP', 'notFinal'].includes(filter.billed.id)) {
      return true;
    }

    // If unmarking as billed and we're filtering for billed entries  
    if (!entry.billed && ['Final', 'Interim'].includes(filter.billed.id)) {
      return true;
    }
    return false;
  };

  /**
   * Determines if an entry can be marked or unmarked as billed
   * @param {Object} entry - The time entry object
   * @returns {boolean} - Whether the mark/unmark option should be shown
   */
  const canMarkUnmarkEntry = entry => {
    // If entry is not billed and not in an invoice, allow "Mark as Billed"
    if (!entry.billed && !entry.invoice?.id) {
      return true;
    }
    // If entry is billed, only allow "Unmark as Billed" for manually marked entries
    if (entry.billed && !entry.invoice?.id) {
      return true; // Manually marked as billed - can be unmarked
    }
    return false;
  };

  /**
   * Handles the mark/unmark as billed action for an entry
   * @param {Object} entry - The time entry object
   * @returns {Promise<boolean>} - Returns true if operation was successful, false otherwise
   */
  const handleMarkAsBilled = async entry => {
    try {
      const markAsBilled = !entry.billed; // Toggle the current billed status

      // Determine if entry should be removed from current view
      const shouldRemove = shouldRemoveEntryAfterBilledChange({
        ...entry,
        billed: markAsBilled
      }, currentFilter);
      await entryStore.markAsBilled(entry.id, markAsBilled, shouldRemove);
      handleSuccess(markAsBilled ? 'Time entry marked as billed successfully' : 'Time entry unmarked as billed successfully');
      return true;
    } catch (error) {
      handleError('Failed to update entry billed status');
      return false;
    }
  };
  return {
    canMarkUnmarkEntry,
    handleMarkAsBilled,
    shouldRemoveEntryAfterBilledChange
  };
}